import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLang } from "@/lib/useLang";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 20_000;

export const SpeaksStatsBar = ({ refreshKey = 0 }) => {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [categoryMap, setCategoryMap] = useState({});
  const prevRef = useRef({ messages: null, upvotes: null });
  const [bump, setBump] = useState({ messages: false, upvotes: false });

  const fetchStats = () => {
    axios.get(`${API}/kerala-speaks-stats`)
      .then((r) => {
        setStats((cur) => {
          const next = r.data;
          // Trigger flash animation only when the number actually changes upward
          const prev = prevRef.current;
          const flashKeys = { messages: false, upvotes: false };
          if (prev.messages !== null && next.total_messages > prev.messages) flashKeys.messages = true;
          if (prev.upvotes !== null && next.total_upvotes > prev.upvotes) flashKeys.upvotes = true;
          prevRef.current = { messages: next.total_messages, upvotes: next.total_upvotes };
          if (flashKeys.messages || flashKeys.upvotes) {
            setBump(flashKeys);
            setTimeout(() => setBump({ messages: false, upvotes: false }), 900);
          }
          return next;
        });
      })
      .catch(() => {});
  };

  // Initial + manual refresh
  useEffect(() => { fetchStats(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [refreshKey]);

  // Live polling — only while tab is visible
  useEffect(() => {
    let id = null;
    const start = () => {
      if (id) return;
      id = setInterval(fetchStats, POLL_INTERVAL_MS);
    };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => {
      if (typeof document === "undefined") return;
      if (document.visibilityState === "visible") {
        fetchStats(); // immediate catch-up on tab focus
        start();
      } else {
        stop();
      }
    };
    if (typeof document !== "undefined") {
      if (document.visibilityState === "visible") start();
      document.addEventListener("visibilitychange", onVis);
    }
    return () => {
      stop();
      if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  useEffect(() => {
    axios.get(`${API}/categories`).then((r) => {
      const m = {};
      (r.data?.categories || []).forEach((c) => { m[c.id] = c; });
      setCategoryMap(m);
    }).catch(() => {});
  }, []);

  if (!stats) return null;
  const topCatLabel = stats.top_category
    ? (lang === "ml" ? categoryMap[stats.top_category]?.name_ml : categoryMap[stats.top_category]?.name) || stats.top_category
    : "—";

  return (
    <div
      className="grid grid-cols-3 gap-3 md:gap-4"
      data-testid="speaks-stats-bar"
    >
      <Stat label={t("stats_messages")} value={stats.total_messages.toLocaleString()} bg="#0A0A0A" ink="#FF9933" testId="stat-messages" bump={bump.messages} />
      <Stat label={t("stats_upvotes")} value={stats.total_upvotes.toLocaleString()} bg="#FF9933" ink="#0A0A0A" testId="stat-upvotes" bump={bump.upvotes} />
      <Stat label={t("stats_top_category")} value={topCatLabel} bg="#138808" ink="#FFFFFF" testId="stat-top-category" />
    </div>
  );
};

const Stat = ({ label, value, bg, ink, testId, bump }) => (
  <div
    className={`brutal-card p-3 md:p-4 text-center transition-transform duration-300 ${bump ? "stat-bump" : ""}`}
    style={{ background: bg, color: ink }}
    data-testid={testId}
    aria-live="polite"
  >
    <p className="text-[10px] md:text-xs uppercase font-bold tracking-[0.25em] opacity-80">
      {label}
    </p>
    <p
      className="font-display uppercase leading-none mt-1 break-words"
      style={{ fontSize: "clamp(1.25rem, 3vw, 2rem)" }}
    >
      {value}
    </p>
  </div>
);

export default SpeaksStatsBar;
